// ForLearners.js
import React, { useEffect, useRef, useState } from "react";
import SEO from "../components/seo";
import { Container } from "reactstrap";
import { Table, TableHeader, TableBody, TableRow, TableCell } from "grommet";
import TitleBannerEnquiry from "../components/titleBanner/TitleBannerEnquiry";
import { getForLearnersPageJsonLd } from "../utils/jsonld";
import "../styles/ForLearners.css";

function useAutoHeight(isOpen) {
    const ref = useRef(null);
    const [h, setH] = useState(0);
    const [dur, setDur] = useState(360);

    const compute = () => {
        const el = ref.current;
        if (!el) return { height: 0, duration: 360 };
        const height = isOpen ? el.scrollHeight + 2 : 0;
        const duration = Math.min(720, Math.max(240, 300 + height * 0.4));
        return { height, duration };
    };

    useEffect(() => {
        const { height, duration } = compute();
        setH(height);
        setDur((d) => (d === duration ? d : duration));
    }, [isOpen]);

    useEffect(() => {
        const el = ref.current;
        if (!el) return;
        let raf = 0;
        const ro = new ResizeObserver(() => {
            cancelAnimationFrame(raf);
            raf = requestAnimationFrame(() => {
                const { height, duration } = compute();
                setH((prev) => (prev === height ? prev : height));
                setDur((d) => (d === duration ? d : duration));
            });
        });
        ro.observe(el);
        return () => {
            cancelAnimationFrame(raf);
            ro.disconnect();
        };
    }, [isOpen]);

    return { ref, style: { maxHeight: h, "--dd-dur": `${dur}ms` } };
}

const DropdownSection = ({
    title,
    children,
    level = 2,
    defaultOpen = false,
    externalResetSignal,
}) => {
    const [isOpen, setIsOpen] = React.useState(!!defaultOpen);
    const [resetSeq, setResetSeq] = React.useState(0);
    const [noAnim, setNoAnim] = React.useState(false);

    const lvl = Math.min(6, Math.max(2, level));
    const HeadingTag = `h${lvl}`;
    const titleId = `dd-title-${title.replace(/\s+/g, "-").toLowerCase()}`;
    const panelId = `dd-panel-${title.replace(/\s+/g, "-").toLowerCase()}`;

    const { ref, style } = useAutoHeight(isOpen);

    useEffect(() => {
        if (externalResetSignal === undefined) return;
        setNoAnim(true);
        setIsOpen(false);
        setResetSeq((s) => s + 1);
        const t = setTimeout(() => setNoAnim(false), 60);
        return () => clearTimeout(t);
    }, [externalResetSignal]);

    const handleToggle = () => {
        setIsOpen((prev) => {
            const next = !prev;
            setResetSeq((s) => s + 1);
            return next;
        });
    };

    return (
        <section className="section" aria-labelledby={titleId}>
            <div className="header-bar">
                <span className={`left-accent ${isOpen ? "open" : ""}`} />
                <button
                    type="button"
                    className="title-button"
                    aria-expanded={isOpen}
                    aria-controls={panelId}
                    id={titleId}
                    onClick={handleToggle}
                    title={`Toggle ${title}`}
                >
                    <HeadingTag className="title-text">{title}</HeadingTag>
                    <span className={`chevron ${isOpen ? "open" : ""}`} aria-hidden="true">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
                            xmlns="http://www.w3.org/2000/svg">
                            <path d="M6 9l6 6 6-6" stroke="currentColor" strokeWidth="2"
                                strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                    </span>
                </button>
            </div>

            <div
                id={panelId}
                role="region"
                aria-labelledby={titleId}
                aria-hidden={!isOpen}
                className={`dropdown-surface ${isOpen ? "open" : ""} ${noAnim ? "no-anim" : ""}`}
                style={style}
            >
                <div ref={ref} className="surface-inner">
                    {React.Children.map(children, (child) =>
                        React.isValidElement(child)
                            ? React.cloneElement(child, { externalResetSignal: resetSeq })
                            : child
                    )}
                </div>
            </div>
        </section>
    );
};

const downloadcert = () => {
    return (
        <>
            <SEO
                title="BIM & CAD Training Center | Ace Industrial Academy"
                description="Ace Industrial Academy is a leading training center in Singapore for IT courses, BIM courses, 3D CAD courses, 4D courses, software courses, building information modeling courses and more."
                jsonLd={getForLearnersPageJsonLd()}
            />
            <TitleBannerEnquiry
                title="Download My Certificate"
                description="Instructions on how you can retrieve your certificates."
                withOnlyDescription
                hasPlainBanner
            />

            <div className="motion-guard">
                <div className="page-bg">
                    <Container fluid className="py-5" style={{ backgroundColor: "transparent" }}>
                        <Container className="py-3">
                            {/* <h3>Download My Certificate</h3>
                            <p className="mt-3">
                                Instructions on how you can retrieve your certificates.<br /><br />
                                Do approach the training coordinator at training@aia.edu.sg should you have any questions or require further assistance. <br /><br />
                            </p> */}

                            <DropdownSection title="WSQ Certificate">
                                <DropdownSection title="Singaporeans/SPR" level={3}>
                                    <p>
                                        For <strong>Singaporeans/SPR</strong> participants: Please access your eSOA via <a href="https://www.myskillsfuture.gov.sg/content/portal/en/index.html">Myskillsfuture.gov.sg</a><br /><br />
                                        Upon successful login, you will redirected to the Skills Passport page, where you can view your
                                        list of learning achievements. To download the Opencert, you can check out further steps <a href="https://www.myskillsfuture.gov.sg/content/portal/en/header/faqs/SkillsPassport.html">here</a>.
                                    </p>
                                </DropdownSection>

                                <DropdownSection title="Non-Singaporeans/SPR" level={3}>
                                    <p>
                                        For <strong>NON-Singaporeans/SPR</strong> participants: Please access your eSOA following the below instructions;<br /><br />
                                        Step 1: Visit <a href="https://www.myskillsfuture.gov.sg/content/portal/en/foreigner/download-cert.html">Download Certificates | Myskillsfuture.gov.sg</a><br /><br />
                                        Step 2: Select ID Type and enter the relevant information to retrieve your certificates. For ID Types:<br /><br />
                                        <ol style={{ marginLeft: "2rem", marginTop: "0.5rem" }}>
                                            <li>1. Select “Previous NRIC / FIN” for ex-Singaporean or FIN-holder</li>
                                            <li>2. Select “Passport Number/Foreign ID” for foreign passport or identification</li><br />
                                        </ol>
                                        Step 3: Then, select Certificate Type “<strong>WSQ Statement of Attainment</strong>” and indicate under Institution UEN “<strong>200710151K</strong>”<br /><br />
                                        Step 4: Click on Find Cert, you will be able to view and download your certificates from the “Certificates” tab.<br /><br />
                                    </p>
                                </DropdownSection>
                            </DropdownSection>

                            <DropdownSection title="Autodesk Certificate">
                                <p>
                                    <em> *You may skip Step 1 if you have an existing Autodesk account.</em><br /><br />
                                    <strong>Step 1: Creating an Autodesk Account</strong><br /><br />
                                    1. Visit this link: <a href="https://education.autodesk.com/register">https://education.autodesk.com/register</a><br />
                                    2. Enter your details accordingly. <strong>*DO NOT TICK "Are you a Student at a Qualified Educational Institution?"</strong><br />
                                    3. Click '<strong>Next</strong>'<br />
                                    4. Read through the terms and conditions and click '<strong>Register</strong>'<br />
                                    5. <strong>TICK "I want to</strong> receive email emails........" and click '<strong>OK</strong>'<br /><br /><br />

                                    <strong>Step 2: Answering Survey</strong><br /><br />
                                    1. Login to your Autodesk account: https://education.autodesk.com/login-student<br />
                                    2. Click '<strong>Course</strong>' and '<strong>Search Course</strong>'<br />
                                    3. Enter the course ID* and click '<strong>Search</strong>'<br />
                                    4. Click '<strong>Enroll This Course</strong>'<br />
                                    5. Double check on the course title and click '<strong>Yes</strong>'<br />
                                    6. Click '<strong>Not Completed</strong>' and complete the survey<br />
                                    Course ID* can be found in the email <span style={{ color: "red" }}>(red)</span><br /><br /><br />

                                    <strong>Step 3: Download Certificate</strong><br /><br />
                                    1. Click '<strong>Finished Course</strong>'<br />
                                    2. Click '<strong>Download here</strong>'<br /><br />
                                    The certificate will be available for download once the training centre has approved your request.<br /><br /><br />

                                    <strong><em>Kindly complete the following within 2 weeks upon receiving this email.</em></strong><br />
                                    <strong><em>Please note that the Course ID will expire within 2 weeks, after which the certificate will no longer be retrievable.</em></strong><br /><br />
                                </p>
                            </DropdownSection>

                            <DropdownSection title="Bentley Certificate">
                                <p>
                                    <strong>Step 1:</strong> Navigate to <a href="https://learning.bentley.com/">https://learning.bentley.com/</a><br /><br />
                                    <strong>Step 2:</strong> Login/ create an account using your registered email address (Please use the same email address that was provided during Course Registration)<br /><br />
                                    <strong>Step 3:</strong> Click on 'User Menu' on the left of the screen<br /><br />
                                    <strong>Step 4:</strong> Click 'My Activities' section<br /><br />
                                    <strong>Step 5:</strong> Click on 'Courses' & Click on the 'Icon' to 'Download Certificate'<br /><br />
                                </p>
                            </DropdownSection>

                            <Container className="pt-4 px-0">
                                <h3>Technical Support</h3>
                                <p style={{ marginLeft: "0px" }}>
                                    <em>Do approach the training coordinator at <a href="mailto:training@aia.edu.sg">training@aia.edu.sg</a> should you have any questions or require further assistance.</em>
                                    <br /><br />
                                </p>
                            </Container>
                        </Container>
                    </Container>
                </div>
            </div>
        </>
    );
};
export default downloadcert;
